import { NavLink, useNavigate } from "react-router-dom";
import { LogOut, Store, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth, mobileEntry } from "@/context/AuthContext";
import { useMobile } from "@/context/MobileContext";
import { ROLE_LABELS, maskDoc, digits } from "@/lib/api";
import { APP_VERSION, MAIN_SCREENS, CONFIG_SCREENS, screensFor } from "@/lib/mobile";
import { cn } from "@/lib/utils";

function DrawerLink({ s, onClick, badge }) {
  const Icon = s.icon;
  return (
    <NavLink to={s.to} end={s.end} onClick={onClick} data-testid={`m-drawer-${s.to.split("/").pop() || "inicio"}`}
      className={({ isActive }) => cn("mx-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
        isActive ? "bg-[#e3f2fd] text-[#0d47a1]" : "text-slate-700 hover:bg-slate-100")}>
      <Icon className="h-5 w-5 text-[#1565c0]" />
      <span className="flex-1">{s.label}</span>
      {badge > 0 && <span className="rounded-full bg-red-500 px-2 text-[11px] font-bold text-white">{badge}</span>}
    </NavLink>
  );
}

const Section = ({ children }) => <p className="px-5 pb-1 pt-4 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">{children}</p>;

export function MDrawer() {
  const { drawer, setDrawer, unread, pendingApprovals } = useMobile();
  const { session, logout } = useAuth();
  const nav = useNavigate();
  const u = session.user;
  const t = session.tenant;
  const close = () => setDrawer(false);
  const out = async () => { close(); await logout(); nav(mobileEntry(), { replace: true }); };
  const doc = digits(t.documento || "");
  const badges = { "/m/liberacao": pendingApprovals, "/m/notificacoes": unread };
  return (
    <Sheet open={drawer} onOpenChange={setDrawer}>
      <SheetContent side="left" className="flex w-[300px] flex-col gap-0 border-0 bg-white p-0 text-slate-900 [&>button]:text-white [&>button]:opacity-90" data-testid="m-drawer">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="bg-[#1565c0] px-5 pb-5 pt-8 text-white">
          <span className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-white/15"><Store className="h-6 w-6" /></span>
          <p className="font-display text-lg font-bold leading-tight" data-testid="m-drawer-company">{t.nome_fantasia}</p>
          <p className="text-xs text-white/80" data-testid="m-drawer-cnpj">{doc.length === 11 ? "CPF" : "CNPJ"}: {doc ? maskDoc(doc) : "não informado"}</p>
          <p className="mt-3 flex items-center gap-1.5 text-sm" data-testid="m-drawer-user"><UserRound className="h-4 w-4" />{u.name} · {ROLE_LABELS[u.role]}</p>
        </div>
        <nav className="flex-1 overflow-y-auto pb-3">
          <Section>Menu principal</Section>
          {screensFor(u, MAIN_SCREENS, t).map((s) => <DrawerLink key={s.to} s={s} onClick={close} badge={badges[s.to]} />)}
          <Section>Configurações</Section>
          {CONFIG_SCREENS.map((s) => <DrawerLink key={s.to} s={s} onClick={close} badge={badges[s.to]} />)}
          <button onClick={out} className="mx-2 mt-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50" data-testid="m-drawer-logout">
            <LogOut className="h-5 w-5" />Sair
          </button>
        </nav>
        <div className="bg-[#0b1f3a] px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-white" data-testid="m-drawer-footer">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">Chave de Acesso</p>
          <p className="font-mono text-lg font-bold tracking-widest" data-testid="m-drawer-access-key">{t.access_number}</p>
          <p className="text-[11px] text-white/60" data-testid="m-drawer-version">Versão {APP_VERSION}</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
